/**
 * Structured logging utility with correlation IDs
 * 
 * This provides structured logging that can be easily parsed and searched
 * in production environments. Correlation IDs help track requests across services.
 */

export interface LogContext {
  correlationId?: string;
  userId?: string;
  sessionId?: string;
  [key: string]: unknown;
}

class Logger {
  private generateCorrelationId(): string {
    return `corr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private formatLog(level: string, message: string, context?: LogContext, error?: Error): string {
    interface LogEntry {
      timestamp: string;
      level: string;
      message: string;
      error?: {
        name: string;
        message: string;
        stack?: string;
      };
      [key: string]: unknown;
    }

    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      ...context,
    };

    if (error) {
      logEntry.error = {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }

    return JSON.stringify(logEntry);
  }

  /**
   * Log debug message
   */
  debug(message: string, context?: LogContext) {
    if (import.meta.env.DEV) {
      console.debug(this.formatLog('DEBUG', message, context));
    }
  }

  /**
   * Log info message
   */
  info(message: string, context?: LogContext) {
    console.info(this.formatLog('INFO', message, context));
  }

  /**
   * Log warning message
   */
  warn(message: string, context?: LogContext, error?: Error) {
    console.warn(this.formatLog('WARN', message, context, error));
  }

  /**
   * Log error message
   */
  error(message: string, context?: LogContext, error?: Error) {
    console.error(this.formatLog('ERROR', message, context, error));
    
    // Send to Sentry if available
    if (typeof window !== 'undefined' && error) {
      try {
        // Dynamic import to avoid circular dependencies
        import('@/lib/sentry').then(({ captureException }) => {
          captureException(error, context);
        }).catch(() => {
          // Sentry not available, ignore
        });
      } catch {
        // Ignore if Sentry is not available
      }
    }
  }

  /**
   * Create a child logger with persistent context (correlation ID, user ID, etc.)
   */
  child(context: LogContext): Logger {
    const childLogger = new Logger();
    const originalMethods = {
      debug: childLogger.debug.bind(childLogger),
      info: childLogger.info.bind(childLogger),
      warn: childLogger.warn.bind(childLogger),
      error: childLogger.error.bind(childLogger),
    };

    // Wrap methods to include parent context
    childLogger.debug = (message: string, additionalContext?: LogContext) => {
      originalMethods.debug(message, { ...context, ...additionalContext });
    };
    childLogger.info = (message: string, additionalContext?: LogContext) => {
      originalMethods.info(message, { ...context, ...additionalContext });
    };
    childLogger.warn = (message: string, additionalContext?: LogContext, error?: Error) => {
      originalMethods.warn(message, { ...context, ...additionalContext }, error);
    };
    childLogger.error = (message: string, additionalContext?: LogContext, error?: Error) => {
      originalMethods.error(message, { ...context, ...additionalContext }, error);
    };

    return childLogger;
  }

  /**
   * Create a logger with a new correlation ID
   */
  withCorrelationId(correlationId?: string): Logger {
    return this.child({ correlationId: correlationId || this.generateCorrelationId() });
  }
}

export const logger = new Logger();







